import { Section } from '@common/components/BookingForms';
import { changeJourneyParam } from '@store/yourJourney/actions';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { SpaPackageType } from 'shared-types/SpaPackage';
import { SpaPackageFormValues } from '../../../SpaPackageForm.schema';
import { DateSelector } from './DateSelector/DateSelector';
import type { DispatchProps as DateSelectorDispatchProps } from './DateSelector/DateSelector.types';
import { FormBox } from './DateTimeSelector.styles';
import { TimeslotSelector } from './TimeslotSelector';

type DateTimeSelectorV2Props = {
  entity: SpaPackageType;
  onStartDateChange: (startDate: string) => void;
  selectedDate: string;
  selectedTime: { label: string; value: string } | undefined;
  loadingDates: string[];
  clearTimeSlots: () => void;
  isLoading: boolean;
  hideArrivalTimes: boolean;
};

export const DateTimeSelectorV2: FC<DateTimeSelectorV2Props> = ({
  entity,
  loadingDates,
  onStartDateChange,
  selectedDate,
  selectedTime,
  clearTimeSlots,
  isLoading,
  hideArrivalTimes,
}) => {
  const { supportsTimeslots = false, timeslots } = entity || {};
  const { control, setValue } = useFormContext<SpaPackageFormValues>();
  const dispatch = useDispatch();
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const activeDate = moment(journeyParams.spaDate);

  const setActiveDate = async (value: moment.Moment) => {
    // changeJourneyParam is a thunk — no typed dispatch hook available
    dispatch(changeJourneyParam('spaDate', value) as any);
  };

  const change = (fieldName: string, value: string) => {
    setValue(fieldName as Path<SpaPackageFormValues>, value, { shouldValidate: true });
  };

  useEffect(() => {
    if (selectedDate) {
      clearTimeSlots();
      const availableTimeslots = timeslots?.[selectedDate];
      if (availableTimeslots && availableTimeslots.length > 0) {
        setValue('arrivalTime', availableTimeslots[0], { shouldValidate: true });
      }
    }
  }, [selectedDate, timeslots]);

  return (
    <Section>
      <FormBox>
        <DateSelector
          entity={entity}
          activeDate={activeDate}
          setActiveDate={setActiveDate}
          clearFormField={(() => {}) as unknown as DateSelectorDispatchProps['clearFormField']}
          change={change as unknown as DateSelectorDispatchProps['change']}
          onStartDateChange={onStartDateChange}
          loadingDates={loadingDates}
          selectedDate={selectedDate}
          isLoading={isLoading}
        />
        <Controller
          name="arrivalTime"
          control={control}
          render={({ field }) => (
            <TimeslotSelector
              onChange={field.onChange}
              display={supportsTimeslots && !hideArrivalTimes}
              timeslotOptions={timeslots?.[selectedDate] ?? []}
              selectedTime={selectedTime ?? { label: '', value: '' }}
              clearTimeSlots={clearTimeSlots}
            />
          )}
        />
      </FormBox>
    </Section>
  );
};
